import React, { FC, useEffect, useState } from 'react';

import { Box, SxProps, Theme } from '@mui/material';

import {
  getUserAvatarFallbackColors,
  getUserAvatarUrl,
} from '~app/utils/transformers/user-avatar-url';

type ResponsiveSize = Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number>;

type Props = {
  size?: Partial<ResponsiveSize> | number;
  userId?: number;
  sx?: SxProps<Theme>;
};

export const UserAvatar: FC<Props> = ({ size = 40, userId, sx }) => {
  const safeUserId = userId ?? 0;
  const [isImageFailed, setIsImageFailed] = useState(false);
  const fallbackColors = getUserAvatarFallbackColors(safeUserId);

  useEffect(() => {
    setIsImageFailed(false);
  }, [safeUserId]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: '50%',
        overflow: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        background: `linear-gradient(135deg, ${fallbackColors.from}, ${fallbackColors.to})`,

        '&::before': {
          content: '""',
          position: 'absolute',
          top: '22%',
          left: '50%',
          width: '34%',
          height: '34%',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.82)',
          transform: 'translateX(-50%)',
        },

        '&::after': {
          content: '""',
          position: 'absolute',
          left: '50%',
          bottom: '-14%',
          width: '72%',
          height: '52%',
          borderRadius: '50% 50% 0 0',
          backgroundColor: 'rgba(255, 255, 255, 0.82)',
          transform: 'translateX(-50%)',
        },

        img: {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
        },

        border: `1px solid`,
        borderColor: 'fog.85',

        ...sx,
      }}
    >
      {!isImageFailed && (
        <img
          src={getUserAvatarUrl(safeUserId)}
          alt="User Avatar"
          onError={() => setIsImageFailed(true)}
        />
      )}
    </Box>
  );
};
